import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { signIn, confirmSignIn, signOut, getCurrentUser, fetchUserAttributes, UserAttributeKey, fetchAuthSession } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'

// Import other stores for clearing
import { useRBACStore } from './rbac-store'
import useOrgDataStore from './org-store'
import { useSurveyStore } from './survey-store'
import { useParticipantSurveyStore } from './participant-survey-store'
import { setLoginPreference } from '@/utils/loginPreferences'

type ExtendedUserAttributeKey = UserAttributeKey | 'custom:role' | 'custom:uiMode';

export interface AuthSession {
  tokens?: {
    idToken?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface User {
  sub: string
  attributes?: Partial<Record<ExtendedUserAttributeKey, string>>
  cognitoData?: Record<string, unknown>
}

interface AuthState {
  user: User | null
  session: AuthSession | null // Store session info separately
  isLoading: boolean
  error: string | null
  signIn: (email: string) => Promise<void>
  signInWithPassword: (email: string, password: string) => Promise<void>
  confirmSignIn: (code: string) => Promise<void>
  signOut: () => Promise<void>
  checkAuth: () => Promise<void>
  logout: () => Promise<void>
  /** Clear Zustand auth + feature stores without waiting on Amplify */
  clearLocalSession: () => void
}

// Function to clear all stores
const clearAllStores = () => {
  // Clear RBAC store
  const rbacStore = useRBACStore.getState()
  rbacStore.clearRBAC()
  
  // Clear org store
  const orgStore = useOrgDataStore.getState()
  orgStore.resetDistrictForm()
  orgStore.resetSchoolForm()
  orgStore.setDistrictSearchParams({ name: '', noChamps: false })
  orgStore.setSchoolSearchParams({ parentOrgId: null, name: '' })
  orgStore.clearSelectedDistrict()
  orgStore.clearSelectedSchool()
  // Reset filter panel if it's open
  if (orgStore.isFilterPanelOpen) {
    orgStore.toggleFilterPanel()
  }
  
  // Clear survey store
  const surveyStore = useSurveyStore.getState()
  surveyStore.clearExpandableStates()
  
  // Clear participant survey store
  const participantSurveyStore = useParticipantSurveyStore.getState()
  participantSurveyStore.clearSession()
}

// Check session only
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const checkSession = async (set: any) => {
  try {
    const session = await fetchAuthSession()
    if (session.tokens?.idToken) {
      set({ session, isLoading: false }, false, 'checkSession/success')
      return session
    } else {
      set({ user: null, session: null, isLoading: false }, false, 'checkSession/noSession')
      return null
    }
  } catch (error) {
    console.log('[auth-store][checkSession] Error:', error)
    set({ user: null, session: null, isLoading: false }, false, 'checkSession/failure')
    return null
  }
}

// Fetch user info if needed
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fetchUserInfo = async (set: any) => {
  try {
    const user = await getCurrentUser()
    const attributes = await fetchUserAttributes()
    set({
      user: {
        sub: user.username,
        attributes
      }
    }, false, 'fetchUserInfo/success')
  } catch (error) {
    console.log('[auth-store][fetchUserInfo] Error:', error)
    set({ user: null }, false, 'fetchUserInfo/failure')
  }
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => {
      let isHandlingSignOut = false; // Flag to prevent loops
      
      Hub.listen('auth', async ({ payload }) => {
        // Prevent handling signOut events if we're already processing one
        if (isHandlingSignOut && payload.event === 'signedOut') {
          return;
        }
        
        switch (payload.event) {
          case 'signedIn':
          case 'tokenRefresh':
            await checkSession(set)
            await fetchUserInfo(set)
            break
          case 'signedOut':
            isHandlingSignOut = true;
            set({ user: null, session: null, isLoading: false }, false, 'auth/signedOut')
            // Clear all stores when signed out
            clearAllStores()
            // Reset flag after a short delay
            setTimeout(() => {
              isHandlingSignOut = false;
            }, 100);
            break
          case 'tokenRefresh_failure':
            console.warn('[auth-store] Token refresh failed, logging out user');
            // Dynamic import avoids a circular dependency with authRedirect → auth-store
            void import('@/utils/authRedirect').then(({ handleAuthFailure }) => {
              handleAuthFailure('Token refresh failed');
            });
            break
          case 'customOAuthState':
            console.log('customOAuthState', payload.data)
            break
        }
      })

      return {
        user: null,
        session: null,
        isLoading: false,
        error: null,

        signIn: async (email: string) => {
          try {
            set({ isLoading: true, error: null }, false, 'signIn/isLoading')
            try {
              const currentUser = await getCurrentUser()
              if (currentUser) {
                console.log('ℹ️ User already signed in:', currentUser.username)
                await checkSession(set)
                await fetchUserInfo(set)
                return
              }
            } catch (error) {
              console.log('[auth-store][signIn] Error checking current user:', error)
            }
            await signIn({
              username: email,
              options: {
                authFlowType: 'USER_AUTH',
                preferredChallenge: 'EMAIL_OTP'
              }
            })
            // Save email code preference on successful sign in
            setLoginPreference('email_code')
            set({ isLoading: false }, false, 'signIn/isLoading')
          } catch (error) {
            console.log('[auth-store][signIn] Error:', error)
            const errorMessage = (error as Error).message
            if (errorMessage.includes('There is already a signed in user')) {
              console.log('ℹ️ User already signed in')
              await checkSession(set)
              await fetchUserInfo(set)
            } else {
              set({ error: errorMessage, isLoading: false }, false, 'alreadySignIn/failure')
            }
          }
        },

        signInWithPassword: async (email: string, password: string) => {
          try {
            set({ isLoading: true, error: null }, false, 'signInWithPassword/isLoading')
            try {
              const currentUser = await getCurrentUser()
              if (currentUser) {
                console.log('ℹ️ User already signed in:', currentUser.username)
                await checkSession(set)
                await fetchUserInfo(set)
                return
              }
            } catch (error) {
              console.log('[auth-store][signInWithPassword] Error checking current user:', error)
            }
            await signIn({
              username: email,
              password: password
            })
            await checkSession(set)
            await fetchUserInfo(set)
            // Save password preference on successful sign in
            setLoginPreference('password')
            set({ isLoading: false }, false, 'signInWithPassword/success')
          } catch (error) {
            console.log('[auth-store][signInWithPassword] Error:', error)
            const errorMessage = (error as Error).message
            if (errorMessage.includes('There is already a signed in user')) {
              console.log('ℹ️ User already signed in')
              await checkSession(set)
              await fetchUserInfo(set)
            } else {
              set({ error: errorMessage, isLoading: false }, false, 'signInWithPassword/failure')
            }
          }
        },

        confirmSignIn: async (code: string) => {
          try {
            set({ isLoading: true, error: null }, false, 'confirmSignIn/isLoading')
            await confirmSignIn({ challengeResponse: code })
            await checkSession(set)
            await fetchUserInfo(set)
            // Save email code preference on successful confirmation
            setLoginPreference('email_code')
          } catch (error) {
            console.log('[auth-store][confirmSignIn] Error:', error)
            set({ error: (error as Error).message, isLoading: false }, false, 'confirmSignIn/failure')
          }
        },

        signOut: async () => {
          try {
            set({ isLoading: true, error: null })
            await signOut()
            set({ user: null, session: null, isLoading: false }, false, 'signOut/success')
            // Clear all stores when signing out
            clearAllStores()
          } catch (error) {
            console.log('[auth-store][signOut] Error:', error)
            set({ error: (error as Error).message, isLoading: false })
          }
        },

        checkAuth: async () => {
          set({ isLoading: true, error: null })
          try {
            const session = await checkSession(set)
            if (session && session.tokens?.idToken) {
              await fetchUserInfo(set)
            } else {
              set({ user: null, isLoading: false }, false, 'checkAuth/noUser')
            }
          } catch (error) {
            console.log('[auth-store][checkAuth] Error:', error)
            set({ user: null, isLoading: false }, false, 'checkAuth/error')
          }
        },


        logout: async () => {
          try {
            set({ isLoading: true, error: null })
            await signOut()
            set({ user: null, session: null, isLoading: false }, false, 'logout/success')
            // Clear all stores when logging out
            clearAllStores()
          } catch (error) {
            console.log('[auth-store][logout] Error:', error)
            set({ error: (error as Error).message, isLoading: false })
          }
        },

        clearLocalSession: () => {
          set(
            { user: null, session: null, isLoading: false, error: null },
            false,
            'clearLocalSession',
          )
          clearAllStores()
        },
      }
    },
    {
      name: 'auth-store',
      enabled: import.meta.env.DEV // Only enable devtools in development
    }
  )
) 